import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  Injector,
  signal,
  WritableSignal,
} from '@angular/core';
import { ColumnState } from 'ag-grid-community';
import { LoggingService } from '@daisi/services/logging/logging.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgGridFilterModel } from '@daisi/models/ag-grid-filter-modal';
import { tap } from 'rxjs';
import { DaisiFeature } from '@daisi/models/daisi-feature-collection';
import { UserStateControllerService } from '@generated/backend-api/api/user-state-controller.service';
import { UserStateDto } from '@generated/backend-api/model/user-state-dto';
import { ToastService } from '@ndwnu/design-system';
import { FeatureCollection, Polygon } from 'geojson';
import { BackgroundStyle } from '@services/map/abstract-map.service';
import { DAISI_INACTIVE_LAYER, DAISI_LAYER } from '@pages/overview-page/layers/daisi-layer';
import { TabId } from '@pages/overview-page/overview-page.component';
import { FCD_VECTOR_STYLE } from '@pages/overview-page/layers/vector-layers';
import { BRIDGES_SOURCE_ID } from '@pages/overview-page/layers/bridges-layer';
import { TRAFFIC_JAM_DETECTION_UNVERIFIED_SOURCE_ID } from '@pages/overview-page/layers/traffic-jam-detection-unverified-layer';
import { DRIPS_SOURCE_ID } from '@pages/overview-page/layers/drips-layer';
import { DataGeoJsonFeature } from '@models/data-geojson-feature';
import { ClosureFeature } from '@models/closure-feature-collection';
import { PlannedClosureFeature } from '@models/planned-closure-feature-collection';
import { RoadworksFeature } from '@models/roadworks-feature-collection';
import { RoadworksTabId } from '@pages/roadworks-page/roadworks-tabs';
import { MSI_SOURCE_ID } from '@pages/overview-page/layers/msi-layer';

export type IncidentLocationColumns = 'sign' | 'text';

export class PersistentUserCustomFieldsState {
  readonly incidentAreaFilter = signal({
    type: 'FeatureCollection',
    features: [],
  } as FeatureCollection<Polygon>);
}

export class PersistentUserState {
  readonly filters: WritableSignal<AgGridFilterModel> = signal({}, { equal: isObjectEqual });
  readonly columns: WritableSignal<ColumnState[]> = signal([], { equal: isObjectEqual });
  // Roadworks table has its own filter/column state so it does not clash with the incident table.
  readonly roadworksFilters: WritableSignal<AgGridFilterModel> = signal(
    {},
    { equal: isObjectEqual },
  );
  readonly roadworksColumns: WritableSignal<ColumnState[]> = signal([], { equal: isObjectEqual });
  readonly bridgesFilters: WritableSignal<AgGridFilterModel> = signal({}, { equal: isObjectEqual });
  readonly bridgesColumns: WritableSignal<ColumnState[]> = signal([], { equal: isObjectEqual });
  // Whether the incident table shows the location as a hectometre sign or as the road and
  // hectometre text columns; the two options exclude each other.
  readonly incidentLocationColumns = signal<IncidentLocationColumns>('sign');

  readonly mapHeightPercentage = signal(30);
  readonly mapWidthPercentage = signal(30);
  readonly verticalLayoutOnDesktop = signal(false);
  readonly notificationSoundVolume = signal(1);
  readonly muteNotificationSounds = signal(true);
  readonly notificationSoundsEnabled = computed(
    () => this.notificationSoundVolume() > 0.01 && !this.muteNotificationSounds(),
  );
  readonly incidentAreaFilterEnabled = signal(false);
  readonly pushNotificationsEnabled = signal(false);
  readonly mapBackgroundConfigId = signal(BackgroundStyle.NDW_OSM_DEFAULT.id);
  readonly incidentMapActiveOptions = signal<string[]>([DAISI_LAYER.id, DAISI_INACTIVE_LAYER.id]);
  readonly trafficMapActiveOptions = signal<string[]>([
    BRIDGES_SOURCE_ID,
    TRAFFIC_JAM_DETECTION_UNVERIFIED_SOURCE_ID,
    DRIPS_SOURCE_ID,
    MSI_SOURCE_ID,
  ]);
  readonly roadworksMapActiveOptions = signal<string[]>([FCD_VECTOR_STYLE.id]);
  readonly activeTabId = signal<TabId>('current');
  readonly activeRoadworksTabId = signal<RoadworksTabId>('actual');
}

export class LocalUserState {
  // The incident the overview acts on: highlighted in the table and on the map, targeted by the
  // action bar. The detail side panel never shows a different incident, only this one.
  readonly selectedIncident: WritableSignal<DaisiFeature | undefined> = signal(undefined);
  readonly detailPanelOpen: WritableSignal<boolean> = signal(false);
  readonly openIncident = computed(() =>
    this.detailPanelOpen() ? this.selectedIncident() : undefined,
  );
  readonly sidePanelAnimationFinished = signal(false);
  readonly mapPopup: WritableSignal<MapPopupSettings | undefined> = signal(undefined);
  readonly selectedClosure: WritableSignal<SelectedClosure | undefined> = signal(undefined);
  readonly selectedRoadworks: WritableSignal<RoadworksFeature | undefined> = signal(undefined);
  // The selected bridge, highlighted in the table and on the map; like selectedIncident, the side
  // panel only shows it while bridgePanelOpen is set. An id rather than the feature: the bridge
  // state keeps changing while it is selected.
  readonly selectedBridgeId: WritableSignal<string | undefined> = signal(undefined);
  readonly bridgePanelOpen: WritableSignal<boolean> = signal(false);
  readonly isMobile = signal(false);
  // Closure id consumed by the traffic map to zoom to the closure and open its popover.
  // Reset to undefined once handled.
  readonly deepLinkClosureId: WritableSignal<string | undefined> = signal(undefined);
}

function isObjectEqual(a: any, b: any) {
  return JSON.stringify(a) === JSON.stringify(b);
}

@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  private readonly userStateController = inject(UserStateControllerService);
  private readonly log = inject(LoggingService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);

  private readonly persistentState = new PersistentUserState();
  private readonly persistentCustomFieldsState = new PersistentUserCustomFieldsState();
  readonly state = {
    ...this.persistentState,
    ...this.persistentCustomFieldsState,
    ...new LocalUserState(),

    // computed signals that need both local and persistent state:
    verticalLayout: computed(() => {
      const forceHorizontal = this.state.isMobile() as boolean;
      const verticalOnDesktop = this.state.verticalLayoutOnDesktop() as boolean;
      return !forceHorizontal && verticalOnDesktop;
    }),
    // Single source of truth for "the user has drawn something": the overview toggle, the settings
    // card and the notification card all need it and used to check it in three different ways.
    hasIncidentAreaFilter: computed(
      () => (this.persistentCustomFieldsState.incidentAreaFilter()?.features?.length ?? 0) > 0,
    ),
  };

  initialize() {
    return this.userStateController
      .getUserState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(
        tap({
          next: (response) => {
            if (response && response.state) {
              for (const [key, value] of Object.entries(response.state)) {
                if (key in this.persistentState) {
                  let field = this.persistentState[key as keyof PersistentUserState];
                  if (this.isWritableSignal(field)) {
                    // @ts-ignore
                    field.set(value);
                  }
                }
              }
              for (const [key, value] of Object.entries(response)) {
                if (key in this.persistentCustomFieldsState) {
                  let field =
                    this.persistentCustomFieldsState[key as keyof PersistentUserCustomFieldsState];
                  if (this.isWritableSignal(field) && value !== undefined && value !== null) {
                    // @ts-ignore
                    field.set(value);
                  }
                }
              }
            }
            this.initializeSaveEffect();
          },
          error: (response) => {
            this.log.error('Error while fetching user state', response);
            this.toastService.open(
              'Er is een fout opgetreden bij het ophalen van de gebruikersinstellingen.',
            );
          },
        }),
      );
  }

  private initializeSaveEffect() {
    let saveTimer: ReturnType<typeof setTimeout> | null = null;

    return effect(
      () => {
        const currentState = {};
        for (const [key, value] of Object.entries(this.persistentState)) {
          if (this.isWritableSignal(value)) {
            // @ts-ignore
            currentState[key] = value();
          }
        }
        const stateRequestDto = {
          state: currentState,
        };
        for (const [key, value] of Object.entries(this.persistentCustomFieldsState)) {
          if (this.isWritableSignal(value)) {
            // @ts-ignore
            stateRequestDto[key] = value();
          }
        }

        // Clear existing timer
        if (saveTimer) {
          clearTimeout(saveTimer);
        }

        // Set new timer with debounce delay
        saveTimer = setTimeout(() => {
          this.log.debug('Saving user persistentState', currentState);

          this.userStateController
            .setUserState(stateRequestDto as UserStateDto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              // The settings page promises that changes are applied right away, so a failing save
              // may not stay silent - otherwise the user keeps working with settings that only
              // exist in this tab.
              error: (response) => {
                this.log.error('Error while saving user state', response);
                this.toastService.open(
                  'Er is een fout opgetreden bij het opslaan van de gebruikersinstellingen.',
                  0,
                  'Sluiten',
                  'error',
                );
              },
            });
        }, 1000); // 1 seconde debounce
      },
      { injector: this.injector },
    );
  }

  // this is used to ignore computed signals in the persistentUserState
  private isWritableSignal(signal: any): signal is WritableSignal<any> {
    return 'set' in signal;
  }
}

export interface MapPopupSettings {
  type: MapPopupType;
  sourceId: string;
  feature: DataGeoJsonFeature<any>;
  x: number;
  y: number;
}

export type MapPopupType =
  | 'incident'
  | 'bridge'
  | 'traffic-jam-detection'
  | 'drip'
  | 'msi'
  | 'closure'
  | 'planned-closure'
  | 'sb-closure'
  | 'roadworks';

export interface SelectedClosure {
  feature: ClosureFeature | PlannedClosureFeature;
  type: 'closure' | 'planned-closure';
}
